import React, { useEffect, useRef, useState } from "react";
import MultiImageErrors from "./MultiImageErrors";
import MultiImageFiles from "./MultiImageFiles";
import MultiImageUploadButton from "./MultiImageUploadButton";
import UploadImageIcon from "../UploadImageIcon";
import { failAlert } from "../../../../../utils/alertUtils";
import { useMultiImageData } from "./MultiImageContext";
import { actualFileType } from "../ImageUtils";
import "assets/css/multi-image.css";

import imageCompression from "browser-image-compression";
import { Spinner } from "reactstrap";

const MultiImageUploader = ({
	totalFiles,
	maxFiles,
	handleUpload,
	hideUploadButton,
}) => {
	const {
		imageKeys,
		setImageKeys,
		setImageSources,
		fileUploads,
		setFileUploads,
		setUploadErrors,
	} = useMultiImageData();

	const fileInputRef = useRef(null);
	const [isCompressing, setIsCompressing] = useState(false);
	const [compressionProgress, setCompressionProgress] = useState({ current: 0, total: 0 });

	const updateInput = () => {
		setUploadErrors([]);
		fileInputRef.current.click();
	};

	const uploadImage = async (e) => {
		e.preventDefault();

		const inputFiles = e.target.files;
		const allowedUploads = imageKeys.length + totalFiles + inputFiles.length;

		if (allowedUploads > maxFiles) {
			if (maxFiles - totalFiles - imageKeys.length <= 0) {
				failAlert(
					`A maximum of ${maxFiles} images are allowed. You cannot select any more images to upload.`
				);
			} else {
				failAlert(
					`A maximum of ${maxFiles} images are allowed. You are allowed to upload ${
						maxFiles - totalFiles - imageKeys.length
					} more image(s).`
				);
			}
			return;
		}

		setIsCompressing(true);
		const filesArray = Object.entries(inputFiles).map(arr => arr[1]).filter(Boolean);
		setCompressionProgress({ current: 0, total: filesArray.length });

		// Process files in batches of 10
		const BATCH_SIZE = 10;
		for (let i = 0; i < filesArray.length; i += BATCH_SIZE) {
			const batch = filesArray.slice(i, i + BATCH_SIZE);
			const tasks = batch.map((currentFile) => {
				return new Promise((resolve) => {
					const reader = new FileReader();
					reader.readAsArrayBuffer(currentFile);
					reader.onloadend = (load) => {
						fileLoaded(load, currentFile).then(() => {
							setCompressionProgress(prev => ({ ...prev, current: prev.current + 1 }));
							resolve();
						}).catch(() => {
							setCompressionProgress(prev => ({ ...prev, current: prev.current + 1 }));
							resolve();
						});
					};
					reader.onerror = (err) => {
						fileError(err, currentFile);
						setCompressionProgress(prev => ({ ...prev, current: prev.current + 1 }));
						resolve();
					};
				});
			});

			await Promise.all(tasks);
		}

		setIsCompressing(false);
		setCompressionProgress({ current: 0, total: 0 });
	};

	const fileLoaded = async (e, upload) => {
		const arr = new Uint8Array(e.target.result).subarray(0, 4);
		let header = "";

		for (let i = 0; i < arr.length; i++) {
			header += arr[i].toString(16);
		}

		const acceptedFileType = actualFileType(header);

		if (acceptedFileType === upload.type) {
			await compressAndAccept(upload);
		} else {
			setUploadErrors((prev) => ({
				...prev,
				[upload.name]: ["Only JPEG/JPG and PNG files are allowed"],
			}));
		}
	};

	const compressAndAccept = async (file) => {
		try {
			const options = {
				maxSizeMB: 1,
				maxWidthOrHeight: 1920,
				useWebWorker: true,
			};

			const compressedFile = await imageCompression(file, options);

			console.log(
				`Compressed ${file.name}: from ${(file.size / 1024 / 1024).toFixed(
					2
				)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`
			);

			const reader = new FileReader();
			reader.readAsDataURL(compressedFile);
			reader.onload = (e) => fileAccepted(e, compressedFile);
		} catch (err) {
			console.error("Compression error:", err);
			fileAccepted(null, file); // fallback to original
		}
	};

	const fileAccepted = (e, upload) => {
		setImageSources((prev) => ({
			...prev,
			[upload.name]: e ? e.target.result : URL.createObjectURL(upload),
		}));
		setFileUploads((prev) => ({
			...prev,
			[upload.name]: upload,
		}));
	};

	const fileError = (e, upload) => {
		const errorMessage =
			e?.target?.error?.name === "NotReadableError"
				? "File could not be read, please try again"
				: e?.target?.error?.name || "Unexpected error";

		setUploadErrors((prev) => ({
			...prev,
			[upload.name]: [errorMessage],
		}));
	};

	useEffect(() => {
		const keys = Object.keys(fileUploads);
		setImageKeys(keys);
		// eslint-disable-next-line
	}, [fileUploads]);

	return (
		<div>
			{isCompressing && (
				<div className="d-flex align-items-center gap-2">
					<Spinner size="sm" color="primary" />
					<span>Compressing images {compressionProgress.current}/{compressionProgress.total}…</span>
				</div>
			)}
			<div className="d-flex justify-content-center align-items-center">
				<div className="multi-image-wrapper">
					<MultiImageFiles />
					<div
						role="button"
						className="multi-image-file-upload d-flex justify-content-center align-items-center"
						onClick={updateInput}
					>
						<UploadImageIcon />
						<span>ADD IMAGE</span>
						<input
							ref={fileInputRef}
							type="file"
							onChange={uploadImage}
							multiple
							value=""
							accept=".png, .jpeg, .jpg, .webp"
						/>
					</div>
					<MultiImageErrors />
					<div className="d-flex align-items-center gap-2 mt-2">
						<MultiImageUploadButton
							handleUpload={handleUpload}
							hideUploadButton={hideUploadButton}
							disabled={isCompressing}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MultiImageUploader;
